import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Clock, Star } from "lucide-react";
import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import { BLOG_POSTS, LEGACY_POSTS } from "@/data/blogPosts";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease, delay },
});

// Merge data-driven posts and legacy hardcoded pages into one card list
const ALL_POSTS = [
  ...BLOG_POSTS.map((p) => ({
    href: `/blog/${p.slug}`,
    tag: p.tag,
    title: p.title,
    desc: p.desc,
    readTime: p.readTime,
    featured: p.featured,
    testid: `blog-card-${p.slug}`,
  })),
  ...LEGACY_POSTS.map((p) => ({
    href: p.slug,
    tag: p.tag,
    title: p.title,
    desc: p.desc,
    readTime: p.readTime,
    featured: false,
    testid: p.testid,
  })),
];

const CATEGORIES = ["All", ...new Set(ALL_POSTS.map((p) => p.tag))];

export default function BlogPage() {
  const [category, setCategory] = useState("All");

  const posts = useMemo(
    () => (category === "All" ? ALL_POSTS : ALL_POSTS.filter((p) => p.tag === category)),
    [category]
  );
  const featured = posts.find((p) => p.featured);
  const rest = posts.filter((p) => p !== featured);

  return (
    <div data-testid="blog-page" className="bg-white">
      <SEOHead
        title="Blog & Resources | ApplyStrat"
        description="Deeply researched guides on ATS, referrals, job search strategy, salary negotiation, and interview prep — plus free AI prompts and tools for tech candidates."
        canonical="https://applystrat.com/blog"
      />

      {/* ── HERO ── */}
      <section className="pt-36 pb-12 text-center" style={{ background: "radial-gradient(circle at 50% -20%, rgba(255,82,0,0.08) 0%, transparent 60%)" }}>
        <div className="max-w-2xl mx-auto px-6">
          <motion.div {...fadeUp()}>
            <p className="section-label-orange mb-4">Resources</p>
            <h1 className="text-5xl md:text-6xl font-semibold tracking-tighter text-ink mb-5">The Job Search, Decoded</h1>
            <p className="text-lg text-ink-secondary leading-relaxed">
              Researched, no-fluff guides on how hiring actually works — ATS, referrals, negotiation, interviews — written for serious tech candidates.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── CATEGORY FILTER ── */}
      <section className="pb-10">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div {...fadeUp(0.1)} className="flex flex-wrap items-center justify-center gap-2" data-testid="blog-filters">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                data-testid={`blog-filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  category === cat
                    ? "bg-ink text-white shadow-md scale-105"
                    : "bg-surface-raised text-ink-secondary hover:text-ink hover:bg-border"
                }`}
              >
                {cat}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── POSTS ── */}
      <section className="py-14 pb-32 bg-surface-raised">
        <div className="max-w-5xl mx-auto px-6">
          <AnimatePresence mode="popLayout">
            {/* Featured card — full width */}
            {featured && (
              <motion.div
                key={featured.href}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.4, ease }}
                data-testid={featured.testid}
                className="mb-7"
              >
                <Link to={featured.href}
                  className="group block bg-ink rounded-3xl p-8 md:p-12 relative overflow-hidden hover:shadow-[0_16px_48px_rgba(29,29,31,0.35)] hover:-translate-y-1 transition-all duration-400">
                  <div className="absolute top-0 right-0 w-[340px] h-[340px] bg-brand/20 rounded-full blur-[80px] group-hover:bg-brand/30 transition-colors duration-500" />
                  <div className="relative z-10 max-w-2xl">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand text-white text-xs font-semibold"><Star size={12} /> Featured</span>
                      <span className="px-3 py-1 rounded-full bg-white/10 text-white/80 text-xs font-medium">{featured.tag}</span>
                      <span className="inline-flex items-center gap-1.5 text-white/60 text-xs"><Clock size={13} /> {featured.readTime}</span>
                    </div>
                    <h2 className="text-white font-semibold text-2xl md:text-3xl tracking-tight leading-snug mb-4 group-hover:text-brand-light transition-colors">{featured.title}</h2>
                    <p className="text-white/70 text-sm md:text-base leading-relaxed mb-6">{featured.desc}</p>
                    <span className="inline-flex items-center gap-2 text-brand font-semibold text-sm group-hover:gap-3 transition-all">
                      Read the guide <ArrowRight size={15} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            )}

            {/* Grid */}
            <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-7">
              {rest.map((post, i) => (
                <motion.div
                  key={post.href}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.4, ease, delay: i * 0.05 }}
                  data-testid={post.testid}
                >
                  <Link to={post.href}
                    className="group h-full bg-white rounded-3xl p-8 border border-black/5 shadow-sm hover:shadow-[0_8px_30px_rgba(255,82,0,0.10)] hover:border-brand/20 hover:-translate-y-1 transition-all duration-400 flex flex-col">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="px-3 py-1 rounded-full bg-brand-light text-brand text-xs font-semibold">{post.tag}</span>
                      <span className="inline-flex items-center gap-1.5 text-ink-secondary text-xs"><Clock size={13} /> {post.readTime}</span>
                    </div>
                    <h2 className="text-ink font-semibold text-xl mb-3 leading-snug group-hover:text-brand transition-colors">{post.title}</h2>
                    <p className="text-ink-secondary text-sm leading-relaxed flex-1 mb-6">{post.desc}</p>
                    <span className="inline-flex items-center gap-2 text-brand font-semibold text-sm group-hover:gap-3 transition-all">
                      Read more <ArrowRight size={14} />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Coming soon */}
          <motion.div {...fadeUp(0.2)} className="mt-10 text-center p-10 rounded-3xl border-2 border-dashed border-black/10">
            <p className="text-ink font-medium text-sm mb-1">New guides every month</p>
            <p className="text-ink-secondary text-xs">Company-specific interview strategies, take-home assignment playbooks, and offer-comparison frameworks are next.</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
